import * as React from "react";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import {
  Box,
  CircularProgress,
  DialogContent,
  TextField,
  Typography
} from "@mui/material";
import { FontPreviewBox } from "./fontsPreview";
import { formatFileSize } from "../../lib/format-size";
import { validateFontUpload, FontFiles } from "../../lib/validation";
import AddIcon from "@mui/icons-material/Add";
import { BrandKitGQL } from "@adflow/graphqlgen";
import { useMutation } from "@apollo/client";
import { useFeatureFlags } from "@adflow/utils";

const buttonStyles = {
  boxSizing: "border-box",
  fontWeight: "bold",
  width: "104px",
  height: "48px",
  border: "2px solid transparent",
  ":hover": {
    borderBottom: "2px solid #2750A8"
  }
};

const SUPPORTED_FORMATS = [
  "font/eot",
  "font/otf",
  "font/ttf",
  "font/woff",
  "font/woff2",
  "application/font-woff"
];

const POSSIBLE_FONTS_PREFIXES = ["font/", "application/font-"];

export function FontsUploadForm(props: {
  onSave?: () => void;
  allowed: boolean;
  isUploadToDefault: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [fontFamily, setFontFamily] = React.useState("");
  const handleSetFontFiles = (newFontFiles: FontFiles[]) => {
    setFontFiles(newFontFiles);
    const validateFonts = validateFontUpload(newFontFiles, fontFamily);

    if (!validateFonts.isValid) {
      setErrors(validateFonts);
    } else {
      setErrors({
        isValid: true,
        isFontFamilyValid: true,
        indexPosition: new Set(),
        message: new Set()
      });
    }
  };
  const [fontFiles, setFontFiles] = React.useState<FontFiles[]>([]);
  const [errors, setErrors] = React.useState<{
    isValid: boolean;
    isFontFamilyValid: boolean;
    indexPosition: Set<number>;
    message: Set<string>;
  }>({
    isValid: true,
    isFontFamilyValid: true,
    indexPosition: new Set(),
    message: new Set()
  });

  const [mutateFunction, { loading, error }] = useMutation(
    BrandKitGQL.UPLOAD_ONE_FONT_FAMILY
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setErrors({
      isValid: true,
      isFontFamilyValid: true,
      indexPosition: new Set(),
      message: new Set()
    });
    setFontFamily("");
    setFontFiles([]);
    setOpen(false);
  };

  const handleSave = async () => {
    setErrors({
      isValid: true,
      isFontFamilyValid: true,
      indexPosition: new Set(),
      message: new Set()
    });

    const validateFonts = validateFontUpload(fontFiles, fontFamily);

    if (!validateFonts.isValid) {
      setErrors(validateFonts);
      return;
    }

    const fontFamilyData = {
      data: {
        name: fontFamily,
        fonts: fontFiles
      }
    };

    try {
      await mutateFunction({
        variables: {
          data: fontFamilyData.data
        }
      });
      setFontFamily("");
      setFontFiles([]);
      setOpen(false);
      props.onSave?.();
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const filterName = React.useCallback((name: string) => {
    // remove file extension and replace special characters with space
    return name.split(/\.[^/.]+$/)[0].replace(/[^a-zA-Z0-9]/g, " ");
  }, []);

  const handleNewFiles = React.useCallback(
    (newFiles: FileList) => {
      // create an array out of newFiles
      const newFilesArray = Array.from(newFiles);

      // map over and create an array of objects where each object has the file and variant
      const newFontFiles = newFilesArray.map((file: File) => ({
        variant: filterName(file.name),
        file
      }));

      // add to the fontFiles state
      setFontFiles([...fontFiles, ...newFontFiles]);
    },
    [filterName, fontFiles]
  );

  const handleFontFamily = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFontFamily(event.target.value);
    },
    []
  );

  const handleFontVariantName = React.useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      index: number
    ) => {
      setFontFiles(
        fontFiles.map((fontFile, i) => {
          if (i === index) {
            return {
              ...fontFile,
              variant: event.target.value
            };
          }
          return fontFile;
        })
      );
    },
    [fontFiles]
  );

  return (
    <>
      <Button
        data-testid='upload-font-button'
        disabled={!props.allowed}
        onClick={handleClickOpen}
        variant='contained'
        color='secondary'
        sx={{
          fontWeight: "bold",
          ":hover": {
            backgroundColor: "secondary.main",
            borderBottom: "2px solid #0078D4"
          }
        }}
      >
        {props.isUploadToDefault ? (
          <Box>Upload Default Font</Box>
        ) : (
          <Box>Upload Font</Box>
        )}
      </Button>
      <Modal open={open}>
        <DialogContent>
          <DraggableBox>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBlockEnd: 3
              }}
            >
              <Typography variant='h3'>Upload Font Files</Typography>
              <CloseIcon onClick={handleClose} />
            </Box>

            <FileDropZoneArea
              files={fontFiles.map(f => f.file)}
              onChange={newFiles => handleNewFiles(newFiles)}
            />
            <Typography variant='body1' color={"GrayText"} marginBlockEnd={2}>
              Supported formats:{" "}
              {Array.from(
                new Set(
                  SUPPORTED_FORMATS.map(format =>
                    format.replace(
                      new RegExp(POSSIBLE_FONTS_PREFIXES.join("|"), "g"),
                      ""
                    )
                  )
                )
              ).join(", ")}
            </Typography>

            {!!fontFiles.length && (
              <TextField
                data-testid='font-family-input'
                onMouseDown={event => event.stopPropagation()}
                label='Font Family'
                error={!errors.isFontFamilyValid}
                required
                size='small'
                value={fontFamily}
                onChange={handleFontFamily}
                sx={{ marginBottom: 2 }}
              />
            )}

            <Typography variant='h4' sx={{ marginBlockEnd: 2, height: "22px" }}>
              {fontFiles.length > 0 ? "Uploaded Files" : ""}
            </Typography>

            <Box
              sx={{
                marginBlockEnd: 2,
                flex: 1,
                height: "250px",
                overflowY: "scroll",
                maxWidth: "100%"
              }}
            >
              {fontFiles.map((fontFile, index) => (
                <Box
                  key={index}
                  sx={{
                    alignItems: "start",
                    display: "flex",
                    flexDirection: "column",
                    gap: "17px",
                    marginBlockEnd: 3,
                    marginLeft: 1,
                    ":hover": { "--fill": "#FF0000" }
                  }}
                >
                  <Box
                    sx={{
                      gap: 2,
                      alignItems: "flex-start",
                      display: "flex",
                      flexDirection: "row",
                      paddingTop: 1
                    }}
                  >
                    <svg
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <g clipPath='url(#clip0_2586_2617)'>
                        <path
                          d='M17.8064 6.14355C16.4743 6.14355 15.3906 7.2273 15.3906 8.55935C15.3906 9.8914 16.4743 10.9751 17.8064 10.9751C19.1385 10.9751 20.2222 9.89145 20.2222 8.55935C20.2222 7.22721 19.1385 6.14355 17.8064 6.14355ZM17.8064 10.0376C16.9913 10.0376 16.3281 9.37451 16.3281 8.55935C16.3281 7.7442 16.9913 7.08105 17.8064 7.08105C18.6215 7.08105 19.2847 7.7442 19.2847 8.55935C19.2847 9.37441 18.6216 10.0376 17.8064 10.0376Z'
                          fill='var(--fill, transparent)'
                        />
                        <path
                          d='M17.8233 21.1488C17.8116 21.1202 17.797 21.093 17.7802 21.0677C17.7633 21.042 17.7436 21.018 17.722 20.9965C17.7005 20.9749 17.6766 20.9552 17.6508 20.9384C17.6255 20.9215 17.5983 20.907 17.5697 20.8952C17.5416 20.8835 17.512 20.8741 17.482 20.8685C17.4216 20.8563 17.3597 20.8563 17.2992 20.8685C17.2692 20.8741 17.2397 20.8835 17.2116 20.8952C17.183 20.907 17.1558 20.9215 17.1305 20.9384C17.1047 20.9552 17.0808 20.9749 17.0592 20.9965C17.0377 21.018 17.018 21.042 17.0011 21.0677C16.9842 21.093 16.9697 21.1202 16.958 21.1488C16.9462 21.177 16.9369 21.2065 16.9313 21.2365C16.9252 21.2665 16.9219 21.2974 16.9219 21.3279C16.9219 21.3584 16.9252 21.3893 16.9313 21.4193C16.9369 21.4493 16.9462 21.4788 16.958 21.5069C16.9697 21.5355 16.9842 21.5627 17.0011 21.588C17.018 21.6138 17.0377 21.6377 17.0592 21.6593C17.07 21.67 17.0813 21.6803 17.0931 21.69C17.1051 21.6997 17.1175 21.709 17.1305 21.7174C17.1558 21.7343 17.183 21.7488 17.2116 21.7605C17.2397 21.7723 17.2692 21.7816 17.2992 21.7873C17.3292 21.7934 17.3602 21.7966 17.3906 21.7966C17.4211 21.7966 17.452 21.7934 17.482 21.7873C17.512 21.7816 17.5416 21.7723 17.5697 21.7605C17.5983 21.7488 17.6255 21.7343 17.6508 21.7174C17.6637 21.7089 17.6762 21.6997 17.6881 21.69C17.7 21.6803 17.7113 21.67 17.722 21.6593C17.7436 21.6377 17.7633 21.6138 17.7802 21.588C17.797 21.5627 17.8116 21.5355 17.8233 21.5069C17.835 21.4788 17.8444 21.4493 17.8505 21.4193C17.8566 21.3893 17.8594 21.3584 17.8594 21.3279C17.8594 21.2974 17.8566 21.2665 17.8505 21.2365C17.8444 21.2064 17.835 21.1769 17.8233 21.1488Z'
                          fill='var(--fill, transparent)'
                        />
                        <path
                          d='M10.4463 2.59902C10.4346 2.5709 10.4201 2.54371 10.4032 2.51793C10.3863 2.49262 10.3666 2.46824 10.3451 2.44668C10.3235 2.42512 10.2996 2.40543 10.2738 2.38855C10.2485 2.37168 10.2213 2.35715 10.1927 2.34543C10.1646 2.33371 10.1351 2.3248 10.1051 2.31871C10.0446 2.30652 9.98273 2.30652 9.92227 2.31871C9.89227 2.3248 9.86273 2.33371 9.83414 2.34543C9.80602 2.35715 9.77883 2.37168 9.75352 2.38855C9.72773 2.40543 9.70383 2.42512 9.68227 2.44668C9.6607 2.46824 9.64102 2.49262 9.62367 2.51793C9.6068 2.54371 9.59273 2.5709 9.58102 2.59902C9.5693 2.62715 9.55992 2.65668 9.5543 2.68668C9.5482 2.71715 9.54492 2.74762 9.54492 2.77855C9.54492 2.80902 9.5482 2.83949 9.5543 2.86996C9.55992 2.89996 9.5693 2.92949 9.58102 2.95762C9.59273 2.98574 9.6068 3.01293 9.62367 3.03871C9.64102 3.06402 9.6607 3.0884 9.68227 3.10996C9.70383 3.13152 9.72773 3.15121 9.75352 3.16809C9.77883 3.18496 9.80602 3.19949 9.83414 3.21121C9.86273 3.22293 9.89227 3.23184 9.92227 3.23793C9.95227 3.24402 9.9832 3.2473 10.0137 3.2473C10.0441 3.2473 10.0751 3.24402 10.1051 3.23793C10.1351 3.23184 10.1646 3.22293 10.1927 3.21121C10.2213 3.19949 10.2485 3.18496 10.2738 3.16809C10.2996 3.15121 10.3235 3.13152 10.3451 3.10996C10.3666 3.0884 10.3863 3.06402 10.4032 3.03871C10.4201 3.01293 10.4346 2.98574 10.4463 2.95762C10.458 2.92949 10.4674 2.89996 10.473 2.86996C10.4791 2.83949 10.4824 2.80902 10.4824 2.77855C10.4824 2.74762 10.4791 2.71715 10.473 2.68668C10.4674 2.65668 10.458 2.62715 10.4463 2.59902Z'
                          fill='var(--fill, transparent)'
                        />
                        <path
                          d='M21.381 2.31167C21.0999 1.12344 20.031 0.236328 18.7581 0.236328H4.0508C1.8172 0.236281 0 2.05348 0 4.28708V18.9943C0 20.455 1.16798 21.6472 2.619 21.6882C2.90006 22.8764 3.969 23.7635 5.24194 23.7635H21.3042C22.7907 23.7635 24 22.5542 24 21.0677V17.5306V5.00544C24 3.54481 22.832 2.35259 21.381 2.31167ZM23.0625 21.0678C23.0625 22.0373 22.2737 22.8261 21.3042 22.8261H5.24194C4.49147 22.8261 3.84966 22.3534 3.5978 21.6902C3.52425 21.4966 3.48366 21.2869 3.48366 21.0678V20.7527V18.581L8.86139 9.46269C9.02512 9.18509 9.31711 9.023 9.63745 9.02816C9.95972 9.03369 10.2441 9.20591 10.3982 9.48898L12.7137 13.7406L16.0793 19.9208C16.1644 20.077 16.3253 20.1654 16.4914 20.1654C16.5672 20.1654 16.644 20.147 16.7152 20.1082C16.9425 19.9844 17.0265 19.6997 16.9027 19.4724L15.8105 17.4668C16.29 17.489 16.7617 17.3337 17.1373 17.0221L19.2453 15.2734C19.5834 14.9929 20.0826 15.0052 20.4064 15.3021L20.5164 15.403L21.4539 16.2623L23.0625 17.7369V21.0678ZM23.0625 16.4651L21.4539 14.9905L20.5164 14.2766C19.9056 14.0211 19.1793 14.11 18.6467 14.5518L16.5387 16.3005C16.3069 16.4929 16.0042 16.5706 15.7083 16.5137C15.4124 16.4569 15.1601 16.2725 15.016 16.0079L13.537 13.2922L12.7111 11.7757V11.7757L11.2216 9.04053C10.9039 8.45717 10.3177 8.10209 9.65348 8.09075C8.9895 8.08034 8.39133 8.41423 8.05387 8.98639L3.48361 16.7355V5.00544C3.48361 4.03592 4.27238 3.24716 5.24189 3.24716H8.13839C8.39728 3.24716 8.60714 3.0373 8.60714 2.77841C8.60714 2.51952 8.39728 2.30966 8.13839 2.30966H5.24189C3.75544 2.30966 2.54611 3.51898 2.54611 5.00544V18.453V20.7458C1.64644 20.6695 0.9375 19.9134 0.9375 18.9943V4.28708C0.9375 2.57037 2.33414 1.17378 4.0508 1.17378H18.7581C19.5085 1.17378 20.1503 1.64652 20.4022 2.3097H11.8884C11.6295 2.3097 11.4196 2.51956 11.4196 2.77845C11.4196 3.03734 11.6295 3.2472 11.8884 3.2472H20.5163L21.4538 3.25405C22.3536 3.33027 23.0625 4.08636 23.0625 5.00544V16.4651Z'
                          fill='#00003C'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_2586_2617'>
                          <rect width='24' height='24' fill='white' />
                        </clipPath>
                      </defs>
                    </svg>

                    <Box sx={{ width: "300px" }}>
                      <Typography
                        noWrap
                        variant='body1'
                        color={"CaptionText"}
                        sx={{ lineHeight: "17px" }}
                      >
                        {fontFile.file.name}
                      </Typography>
                      <Typography
                        variant='body1'
                        color={"GrayText"}
                        fontSize={12}
                        lineHeight={"15px"}
                      >
                        {formatFileSize(fontFile.file.size)}
                      </Typography>
                    </Box>

                    <CloseIcon
                      onClick={() => {
                        handleSetFontFiles(
                          fontFiles.filter((_, i) => i !== index)
                        );
                      }}
                    />
                  </Box>

                  <TextField
                    onMouseDown={event => event.stopPropagation()}
                    data-testid='font-variant-input'
                    error={errors.indexPosition.has(index)}
                    size='small'
                    sx={{ width: "380px" }}
                    label='Variant Name'
                    required
                    onChange={event => handleFontVariantName(event, index)}
                    value={fontFile.variant}
                  ></TextField>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                boxSizing: "border-box",
                height: "48px",
                gap: "10px",
                justifyContent: "flex-end"
              }}
            >
              {!errors.isValid && (
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  {Array.from(errors.message).map((message, index) => (
                    <Typography
                      key={index}
                      variant='caption'
                      color='error'
                      data-testid='error-caption'
                    >
                      {message}
                    </Typography>
                  ))}
                </Box>
              )}
              {error && (
                <Typography
                  data-testid='error-caption'
                  variant='caption'
                  color='error'
                >
                  {error.message + " Please try again."}
                </Typography>
              )}
              <Button
                size='medium'
                variant='text'
                color='primary'
                onClick={handleClose}
                sx={buttonStyles}
              >
                Cancel
              </Button>
              <Button
                data-testid='upload-font-submit'
                size='medium'
                variant='contained'
                color='primary'
                disabled={!fontFiles.length}
                onClick={handleSave}
                sx={{
                  ...buttonStyles,
                  ":hover": {
                    backgroundColor: "primary.main",
                    borderBottom: "2px solid #2750A8"
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color={"secondary"} />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          </DraggableBox>
        </DialogContent>
      </Modal>
    </>
  );
}

function CloseIcon(props: { onClick: () => void }) {
  return (
    <Box
      onClick={props.onClick}
      sx={{
        ["--color"]: "#DEDEDE",
        cursor: "pointer",
        ":hover": { ["--color"]: "#cfcfcf" }
      }}
    >
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <circle cx='12' cy='12' r='12' fill='var(--color)' />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M17.6004 7.5279L16.4724 6.3999L12.0004 10.8719L7.52839 6.3999L6.40039 7.5279L10.8724 11.9999L6.40039 16.4719L7.52839 17.5999L12.0004 13.1279L16.4724 17.5999L17.6004 16.4719L13.1284 11.9999L17.6004 7.5279Z'
          fill='white'
        />
      </svg>
    </Box>
  );
}

function FileDropZoneArea(props: {
  files: File[];
  onChange?: (files: any) => void;
}) {
  const [hasDragOver, setHasDragOver] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (!fileInputRef.current) return;

    fileInputRef.current.click();
  };
  return (
    <Box
      onClick={handleButtonClick}
      onDrop={event => {
        event.preventDefault();
        setHasDragOver(false);

        if (!event.dataTransfer.files) return;

        const newFiles = Array.from(event.dataTransfer.files).filter(file =>
          SUPPORTED_FORMATS.includes(file.type)
        ) as File[];

        if (!newFiles) return;

        if (props.onChange) {
          props.onChange(newFiles);
        }
      }}
      onDragOver={event => {
        setHasDragOver(true);
        event.preventDefault();
      }}
      onDragLeave={event => {
        setHasDragOver(false);
        event.preventDefault();
      }}
      sx={{
        cursor: "pointer",
        backgroundColor: hasDragOver ? "#E4F3FF22" : "#ffffff00",
        width: "550px",
        height: "215px",
        display: "flex",
        overflowY: "scroll",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 1
      }}
    >
      <svg
        style={{
          position: "absolute",
          zIndex: -1,
          width: "550px",
          height: "215px"
        }}
        viewBox='0 0 484 180'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect
          x='0.5'
          y='0.5'
          width='463'
          height='179'
          rx='2.5'
          fill='transparent'
          stroke={hasDragOver ? "#556cd6" : "#E3E3E3"}
          strokeDasharray='10 10'
        />
      </svg>

      {props.files.length ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            overflowY: "scroll",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "200px",
            gap: "15px",
            padding: "10px"
          }}
        >
          {props.files.map((f, i) => (
            <FontPreviewBox key={i} fontFile={f} />
          ))}

          {
            <AddIcon
              sx={{ width: 50, height: 50, padding: "10px", margin: "5px" }}
              color='action'
            />
          }
        </Box>
      ) : (
        <>
          <Box>
            <svg
              width='40'
              height='40'
              viewBox='0 0 40 40'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M33.9833 13.796C33.632 7.796 28.686 3 22.7227 3C19.1027 3 15.5973 4.77533 13.4253 7.672C13.376 7.646 13.3233 7.62867 13.2733 7.604C13.1413 7.54 13.0073 7.47867 12.87 7.42467C12.7933 7.39467 12.7167 7.36733 12.6393 7.34C12.5047 7.29267 12.3687 7.25133 12.2293 7.21333C12.152 7.19267 12.0753 7.17133 11.9967 7.15333C11.8473 7.11933 11.6953 7.09333 11.5413 7.07067C11.4733 7.06067 11.4067 7.04733 11.338 7.03933C11.118 7.01533 10.894 7 10.6667 7C7.35867 7 4.66667 9.69133 4.66667 13C4.66667 13.086 4.67133 13.17 4.67733 13.254C1.90467 14.7653 0 17.9327 0 21.1027C0 25.8247 3.84133 29.6667 8.56333 29.6667H12C12.368 29.6667 12.6667 29.3687 12.6667 29C12.6667 28.6313 12.368 28.3333 12 28.3333H8.56333C4.57667 28.3333 1.33333 25.09 1.33333 21.1027C1.33333 18.3333 3.13667 15.4587 5.62133 14.2673L6 14.0867V13.6667C6 13.5847 6.00533 13.5007 6.01 13.4167L6.016 13.3L6.008 13.1747C6.00467 13.1167 6 13.0587 6 13C6 10.4273 8.09333 8.33333 10.6667 8.33333C10.8727 8.33333 11.076 8.35133 11.278 8.378C11.33 8.38467 11.3813 8.39333 11.4327 8.402C11.6113 8.43133 11.7873 8.47 11.9607 8.52C11.9833 8.52667 12.0067 8.53067 12.0287 8.53733C12.2193 8.59533 12.4053 8.66933 12.5873 8.752C12.6333 8.77267 12.6787 8.79533 12.724 8.818C12.8853 8.89733 13.0433 8.98533 13.1953 9.084C14.48 9.91667 15.3333 11.3593 15.3333 13C15.3333 13.3687 15.632 13.6667 16 13.6667C16.368 13.6667 16.6667 13.3687 16.6667 13C16.6667 11.164 15.836 9.52067 14.5333 8.41933C16.444 5.91933 19.592 4.33333 22.7227 4.33333C27.8853 4.33333 32.1747 8.42333 32.6213 13.5913C31.94 13.5433 30.9193 13.5187 29.8993 13.6747C29.5353 13.73 29.2853 14.07 29.3407 14.434C29.3907 14.764 29.6747 15.0007 29.9987 15.0007C30.032 15.0007 30.066 14.998 30.1 14.9927C31.5827 14.7687 33.1287 14.9787 33.2227 14.9913C36.3267 15.5813 38.6667 18.4407 38.6667 21.648C38.6667 25.334 35.6673 28.3333 31.9813 28.3333H29.3333C28.9653 28.3333 28.6667 28.6313 28.6667 29C28.6667 29.3687 28.9653 29.6667 29.3333 29.6667H31.9813C36.4027 29.6667 40 26.0693 40 21.648C40 17.9887 37.4487 14.7067 33.9833 13.796Z'
                fill='#3367D6'
              />
              <path
                d='M21.1382 20.5292C21.0768 20.4672 21.0028 20.4185 20.9208 20.3845C20.7582 20.3172 20.5742 20.3172 20.4115 20.3845C20.3295 20.4185 20.2562 20.4672 20.1942 20.5292L14.8615 25.8618C14.6008 26.1225 14.6008 26.5438 14.8615 26.8045C14.9915 26.9345 15.1622 26.9998 15.3328 26.9998C15.5035 26.9998 15.6742 26.9345 15.8042 26.8045L19.9995 22.6092V36.3332C19.9995 36.7018 20.2982 36.9998 20.6662 36.9998C21.0342 36.9998 21.3328 36.7018 21.3328 36.3332V22.6092L25.5282 26.8045C25.6582 26.9345 25.8288 26.9998 25.9995 26.9998C26.1702 26.9998 26.3408 26.9345 26.4708 26.8045C26.7315 26.5438 26.7315 26.1225 26.4708 25.8618L21.1382 20.5292Z'
                fill='#3367D6'
              />
            </svg>
          </Box>

          <Button
            sx={{
              maxHeight: "32px"
            }}
            variant='outlined'
          >
            Upload Fonts
          </Button>
          <Typography color='GrayText' variant='body1'>
            or drag and drop file here
          </Typography>
        </>
      )}
      <input
        data-testid='upload-font-input'
        type='file'
        multiple
        accept='.eot,.otf,.ttf,.woff,.woff2'
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={event => {
          const files = event.target.files;

          if (!files) return;

          if (props.onChange) {
            props.onChange(files);
          }
        }}
      />
    </Box>
  );
}

const DraggableBox = React.forwardRef(
  (props: React.PropsWithChildren<any>, ref) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [position, setPosition] = React.useState({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    });
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

    const handleMouseDown = (e: any) => {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    };

    const handleMouseMove = (e: any) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    return (
      <Box
        ref={ref}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        sx={{
          position: "absolute",
          left: `${position.x}px`,
          top: `${position.y}px`,
          backgroundColor: "#fff",
          boxShadow: "0px 4px 20px 0px #0000004D",
          display: "flex",
          translate: "-50% -50%",
          userSelect: "none",
          cursor: "move",
          boxSizing: "border-box",
          padding: "26px 34px",
          flexDirection: "column",
          width: "602px",
          height: "90vh",
          maxHeight: "790px",
          borderRadius: "3px"
        }}
      >
        {props.children}
      </Box>
    );
  }
);

DraggableBox.displayName = "DraggableBox";
